export type SchedulePeriodKind = 'explicit' | 'academic-year' | 'unknown';

export interface AcademicYearLike {
    academicYear?: string;
    startWeek?: number;
    endWeek?: number;
    periods?: RawPeriodLike[];
}

export interface RawPeriodLike {
    id?: number | string;
    name?: string;
    startWeek?: number;
    endWeek?: number;
    start_week?: number;
    end_week?: number;
    startWeekYear?: number;
    endWeekYear?: number;
    start_week_year?: number;
    end_week_year?: number;
    year?: number;
    color?: string;
}

export interface ResolvedSchedulePeriod {
    kind: SchedulePeriodKind;
    periodRef: number | null;
    periodId: number | null;
    academicYearStart: number | null;
    label: string;
    startWeek: number | null;
    endWeek: number | null;
    startWeekYear?: number | null;
    endWeekYear?: number | null;
    color?: string;
}

export function parseAcademicYearStart(academicYear?: string): number | null {
    if (!academicYear) return null;
    const match = academicYear.match(/^(\d{4})/);
    return match?.[1] ? parseInt(match[1], 10) : null;
}

function mapWeekToCalendarYearFromStructure(
    weekNumber: number,
    structure: AcademicYearLike
): number {
    const startCal = parseAcademicYearStart(structure.academicYear);
    if (!startCal) {
        return new Date().getFullYear();
    }
    const sw = structure.startWeek ?? 1;
    const ew = structure.endWeek ?? 53;
    if (sw <= ew) {
        return startCal;
    }
    if (weekNumber >= sw) {
        return startCal;
    }
    if (weekNumber <= ew) {
        return startCal + 1;
    }
    return startCal;
}

function inferMinStartYearFromPeriods(periods?: RawPeriodLike[] | null): number | null {
    if (!periods?.length) return null;
    const years = periods
        .map(p => p.year ?? p.startWeekYear ?? p.start_week_year)
        .filter((y): y is number => y != null && !Number.isNaN(Number(y)))
        .map(Number);
    return years.length ? Math.min(...years) : null;
}

function inferWeekYearsFromStructure(
    startWeek: number,
    endWeek: number,
    structure?: AcademicYearLike | null
): { startWeekYear: number; endWeekYear: number } | null {
    const refFromPeriods = inferMinStartYearFromPeriods(structure?.periods);
    if (!structure?.academicYear && refFromPeriods == null) {
        return null;
    }
    const synth: AcademicYearLike = structure?.academicYear
        ? structure
        : {
            ...(structure ?? {}),
            academicYear: `${refFromPeriods}-${(refFromPeriods ?? 0) + 1}`,
            startWeek: structure?.startWeek ?? 1,
            endWeek: structure?.endWeek ?? 53
        };
    const startWeekYear = mapWeekToCalendarYearFromStructure(startWeek, synth);
    const endWeekYear = mapWeekToCalendarYearFromStructure(endWeek, synth);
    return { startWeekYear, endWeekYear };
}

export function normalizePeriod(
    period: RawPeriodLike,
    structure?: AcademicYearLike | null
): ResolvedSchedulePeriod | null {
    const periodId = period.id !== undefined ? Number(period.id) : null;
    const startWeek = period.startWeek ?? period.start_week ?? null;
    const endWeek = period.endWeek ?? period.end_week ?? null;

    if (!periodId || !startWeek || !endWeek) {
        return null;
    }

    let startWeekYear = period.startWeekYear ?? period.start_week_year ?? null;
    let endWeekYear = period.endWeekYear ?? period.end_week_year ?? null;
    if (startWeekYear == null || endWeekYear == null) {
        const inferred = inferWeekYearsFromStructure(startWeek, endWeek, structure);
        if (inferred) {
            if (startWeekYear == null) {
                startWeekYear = inferred.startWeekYear;
            }
            if (endWeekYear == null) {
                endWeekYear = inferred.endWeekYear;
            }
        }
    }

    const academicYearStartFromPeriod = period.year ?? startWeekYear
        ?? parseAcademicYearStart(structure?.academicYear)
        ?? null;

    return {
        kind: 'explicit',
        periodRef: periodId,
        periodId,
        academicYearStart: academicYearStartFromPeriod,
        label: period.name || `Period ${periodId}`,
        startWeek,
        endWeek,
        startWeekYear,
        endWeekYear,
        color: period.color
    };
}

export function createAcademicYearPeriodScope(structure?: AcademicYearLike | null): ResolvedSchedulePeriod | null {
    const fromLabel = parseAcademicYearStart(structure?.academicYear);
    const fromPeriods = inferMinStartYearFromPeriods(structure?.periods);
    const academicYearStart = fromLabel ?? fromPeriods;
    if (academicYearStart == null) {
        return null;
    }

    const startWeek = structure?.startWeek ?? 1;
    const endWeek = structure?.endWeek ?? 53;
    const label =
        structure?.academicYear?.trim()
        || `${academicYearStart}-${academicYearStart + 1}`;

    return {
        kind: 'academic-year',
        periodRef: academicYearStart,
        periodId: null,
        academicYearStart,
        label,
        startWeek,
        endWeek
    };
}

export function resolveScheduleEntryPeriod(
    rawPeriodId: number | string | null | undefined,
    structure?: AcademicYearLike | null
): ResolvedSchedulePeriod {
    const periodRef = rawPeriodId !== null && rawPeriodId !== undefined ? Number(rawPeriodId) : null;
    const academicYearScope = createAcademicYearPeriodScope(structure);

    if (!periodRef || Number.isNaN(periodRef)) {
        return {
            kind: 'unknown',
            periodRef: null,
            periodId: null,
            academicYearStart: academicYearScope?.academicYearStart ?? null,
            label: 'Unknown period',
            startWeek: null,
            endWeek: null,
            startWeekYear: null,
            endWeekYear: null
        };
    }

    const periods = structure?.periods || [];
    for (const rawPeriod of periods) {
        const period = normalizePeriod(rawPeriod, structure);
        if (period && period.periodId === periodRef) {
            return {
                ...period,
                academicYearStart: period.academicYearStart ?? academicYearScope?.academicYearStart ?? null
            };
        }
    }

    return {
        kind: 'unknown',
        periodRef,
        periodId: null,
        academicYearStart: academicYearScope?.academicYearStart ?? null,
        label: `Period ${periodRef}`,
        startWeek: null,
        endWeek: null,
        startWeekYear: null,
        endWeekYear: null
    };
}

export function findFullAcademicYearPeriod(
    legacyYear: number | string | null | undefined,
    structure?: AcademicYearLike | null
): ResolvedSchedulePeriod | null {
    const academicYearScope = createAcademicYearPeriodScope(structure);
    const parsedLegacyYear = legacyYear !== null && legacyYear !== undefined ? Number(legacyYear) : null;

    if (!academicYearScope || !parsedLegacyYear || parsedLegacyYear !== academicYearScope.academicYearStart) {
        return null;
    }

    const periods = structure?.periods || [];
    for (const rawPeriod of periods) {
        const period = normalizePeriod(rawPeriod, structure);
        if (
            period
            && period.startWeek === academicYearScope.startWeek
            && period.endWeek === academicYearScope.endWeek
        ) {
            return {
                ...period,
                kind: 'academic-year',
                academicYearStart: academicYearScope.academicYearStart,
                label: period.label || academicYearScope.label
            };
        }
    }

    return academicYearScope;
}

export function isWeekInWeekRange(week: number, startWeek: number, endWeek: number): boolean {
    if (startWeek <= endWeek) {
        return week >= startWeek && week <= endWeek;
    }

    return week >= startWeek || week <= endWeek;
}

function weekYearTupleOrder(year: number, week: number): number {
    return year * 100 + week;
}

/** True if (targetYear, targetWeek) lies inside the resolved period bounds (including explicit calendar week years). */
export function isResolvedPeriodContainingWeek(
    resolvedPeriod: ResolvedSchedulePeriod | null | undefined,
    targetWeekNumber: number,
    targetYear: number
): boolean {
    if (!resolvedPeriod || resolvedPeriod.kind === 'unknown' || !resolvedPeriod.startWeek || !resolvedPeriod.endWeek) {
        return true;
    }

    const sy = resolvedPeriod.startWeekYear;
    const ey = resolvedPeriod.endWeekYear;
    if (sy != null && ey != null) {
        const t = weekYearTupleOrder(targetYear, targetWeekNumber);
        return t >= weekYearTupleOrder(sy, resolvedPeriod.startWeek)
            && t <= weekYearTupleOrder(ey, resolvedPeriod.endWeek);
    }

    return isWeekInWeekRange(targetWeekNumber, resolvedPeriod.startWeek, resolvedPeriod.endWeek);
}

export function expandWeekSeries(weekSeries?: string | null): number[] {
    if (!weekSeries || typeof weekSeries !== 'string') {
        return [];
    }

    const weeks = new Set<number>();
    weekSeries.split(',').map(part => part.trim()).filter(Boolean).forEach(part => {
        if (part.includes('-')) {
            const [startRaw, endRaw] = part.split('-');
            const start = parseInt((startRaw || '').trim(), 10);
            const end = parseInt((endRaw || '').trim(), 10);
            if (Number.isNaN(start) || Number.isNaN(end) || start < 1 || start > 53 || end < 1 || end > 53) {
                return;
            }

            if (start <= end) {
                for (let week = start; week <= end; week += 1) {
                    weeks.add(week);
                }
            } else {
                for (let week = start; week <= 53; week += 1) {
                    weeks.add(week);
                }
                for (let week = 1; week <= end; week += 1) {
                    weeks.add(week);
                }
            }
            return;
        }

        const week = parseInt(part, 10);
        if (!Number.isNaN(week) && week >= 1 && week <= 53) {
            weeks.add(week);
        }
    });

    return Array.from(weeks).sort((a, b) => a - b);
}

export function getWeeksOutsideResolvedPeriod(
    weekSeries: string | null | undefined,
    period: ResolvedSchedulePeriod | null | undefined
): number[] {
    if (!period?.startWeek || !period?.endWeek) {
        return [];
    }

    return expandWeekSeries(weekSeries).filter(
        week => !isWeekInWeekRange(week, period.startWeek!, period.endWeek!)
    );
}

function sortedWeekNumbersToWeekSeriesString(weeks: number[]): string {
    const sorted = weeks.filter((w, i, arr) => arr.indexOf(w) === i).sort((a, b) => a - b);
    const first = sorted[0];
    if (first === undefined) {
        return '';
    }

    const parts: string[] = [];
    let runStart = first;
    let runEnd = first;

    for (let i = 1; i < sorted.length; i += 1) {
        const w = sorted[i];
        if (w === undefined) {
            continue;
        }
        if (w === runEnd + 1) {
            runEnd = w;
        } else {
            parts.push(runStart === runEnd ? String(runStart) : `${runStart}-${runEnd}`);
            runStart = w;
            runEnd = w;
        }
    }
    parts.push(runStart === runEnd ? String(runStart) : `${runStart}-${runEnd}`);

    return parts.join(',');
}

/**
 * Keeps only weeks that fall within the resolved period's week bounds.
 * If the period has no bounds (e.g. academic-year option with no named period), returns the week series unchanged.
 */
export function intersectWeekSeriesWithPeriod(
    weekSeries: string | null | undefined,
    period: ResolvedSchedulePeriod | null | undefined
): string {
    if (!period?.startWeek || !period?.endWeek) {
        return typeof weekSeries === 'string' ? weekSeries : '';
    }

    const expanded = expandWeekSeries(weekSeries);
    const inside = expanded.filter(week =>
        isWeekInWeekRange(week, period.startWeek!, period.endWeek!)
    );

    return sortedWeekNumbersToWeekSeriesString(inside);
}
